/* Confirming a move off a paid plan. To another paid plan, billing prices it first
   (`change/preview`): an upgrade charges the difference now, a downgrade waits for
   the period's end and can be undone until then. To free, it is a cancellation:
   always at the period's end, so the org keeps what it paid for. */
import { toast } from "sonner";
import { useCancelPlan, useChangePlan, useChangePreview, useRevokeChange } from "@/shared/api/billing";
import { confirmDialogCls, PrimaryButton } from "@/shared/components/settings-atoms";
import {
  AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import { PLANS } from "@/shared/lib/plans";
import { formatCredits, shortDate } from "./format";

import type { BillingPlan, ChangePreview } from "@/shared/types/billing";

function priceLine(preview: ChangePreview | undefined, name: string, current: string) {
  if (!preview) return "Working out the price…";
  if (preview.effective_at) return `You move to ${name} on ${shortDate(preview.effective_at)}. Until then you keep ${current}.`;
  const now = (preview.amount_minor / 100).toLocaleString(undefined, { style: "currency", currency: preview.currency });
  return `${now} is charged now, and ${formatCredits(preview.credits)} credits are added today.`;
}

export default function ChangePlanDialog({ target, currentName, periodEnd, onClose }: {
  target: BillingPlan | null;
  currentName: string;
  periodEnd: string | null;
  onClose: () => void;
}) {
  const cancelling = target?.name === "free";
  const preview = useChangePreview(target && !cancelling ? target.id : null);
  const change = useChangePlan();
  const revoke = useRevokeChange();
  const cancel = useCancelPlan();
  const name = PLANS.find((p) => p.id === target?.name)?.name ?? "";

  const confirm = () => {
    if (!target) return;
    if (cancelling) {
      cancel.mutate(undefined, { onSuccess: () => toast.success(`Your plan ends${periodEnd ? ` on ${shortDate(periodEnd)}` : ""}`), onSettled: onClose });
      return;
    }
    change.mutate(
      { plan_id: target.id, idempotency_key: crypto.randomUUID() },
      {
        onSuccess: (out) => {
          if (out.redirect_url) return;
          if (out.effective_at) {
            toast(`You move to ${name} on ${shortDate(out.effective_at)}`, {
              action: { label: "Undo", onClick: () => revoke.mutate(undefined, { onSuccess: () => toast.success(`You stay on ${currentName}`) }) },
            });
          } else toast.success(`You're on ${name}`);
        },
        onSettled: onClose,
      },
    );
  };

  const busy = change.isPending || cancel.isPending;
  return (
    <AlertDialog open={!!target} onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent className={`${confirmDialogCls} max-w-[420px]`}>
        <AlertDialogHeader>
          <AlertDialogTitle className="text-[15px] tracking-[-0.01em]">
            {cancelling ? `Cancel ${currentName}?` : `Move to ${name}?`}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-[13px] leading-[1.6]">
            {cancelling
              ? `You keep ${currentName} and its credits${periodEnd ? ` until ${shortDate(periodEnd)}` : " until the period ends"}, then move to Free. You can change your mind until then.`
              : preview.isError ? "Billing couldn't price this change. Try again in a moment." : priceLine(preview.data, name, currentName)}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-full text-[13px]">Not now</AlertDialogCancel>
          <PrimaryButton disabled={busy || (!cancelling && !preview.data)} onClick={confirm}>
            {cancelling ? "Cancel plan" : `Move to ${name}`}
          </PrimaryButton>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
